import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useEffect, useState, useMemo } from 'react';
import api from '../../../utils/api';
import { toast } from 'sonner';
import { Card } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../../components/ui/dialog';
import {
  Loader2, ExternalLink, CheckCircle2, ArrowUpDown, ArrowUp, ArrowDown,
  Search, X, RotateCcw, AlertTriangle, Wallet, Filter, Pencil, Paperclip, Trash2, Eye, Upload,
} from 'lucide-react';

const fmtDate = (s) => {
  if (!s) return '-';
  try {
    return new Date(s).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return s; }
};

const fmtEUR = (n) => `€ ${Number(n || 0).toFixed(2).replace('.', ',')}`;

export default function PayPalOwnerPaymentsTab({ readOnly = false }) {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [includePaid, setIncludePaid] = useState(false);
  const [onlyDue, setOnlyDue] = useState(false);
  const [includeCancelled, setIncludeCancelled] = useState(false);
  const [includeZero, setIncludeZero] = useState(false);

  // Filtri colonna
  const [fSearch, setFSearch] = useState(''); // ricerca globale
  const [fOwner, setFOwner] = useState('');
  const [fPaypal, setFPaypal] = useState('');
  const [fProp, setFProp] = useState('');
  const [fBooking, setFBooking] = useState('');
  const [fGuest, setFGuest] = useState('');
  const [fDueFrom, setFDueFrom] = useState('');
  const [fDueTo, setFDueTo] = useState('');
  const [fCheckInFrom, setFCheckInFrom] = useState('');
  const [fCheckInTo, setFCheckInTo] = useState('');
  const [fCheckOutFrom, setFCheckOutFrom] = useState('');
  const [fCheckOutTo, setFCheckOutTo] = useState('');
  const [fAmountMin, setFAmountMin] = useState('');
  const [fAmountMax, setFAmountMax] = useState('');
  const [fStatus, setFStatus] = useState('all'); // all | pending | paid | overdue | cancelled
  const [showFilters, setShowFilters] = useState(false);

  // Sort
  const [sortBy, setSortBy] = useState('owner_payment_due_date');
  const [sortDir, setSortDir] = useState('asc');

  // Multi-select
  const [selected, setSelected] = useState(new Set());

  // Modale conferma
  const [markDialog, setMarkDialog] = useState(null); // { mode: 'single'|'bulk', items: [...] }
  const [markTxId, setMarkTxId] = useState('');
  const [markNotes, setMarkNotes] = useState('');
  const [markBusy, setMarkBusy] = useState(false);
  // Split RSRV/COST modificabili per singolo booking (key: booking_id)
  const [splitOverrides, setSplitOverrides] = useState({}); // { [booking_id]: { rsrv, cost } }
  const [splitsLoading, setSplitsLoading] = useState(false);
  const [splitPreviews, setSplitPreviews] = useState({}); // { [booking_id]: server response }

  // Modale modifica pagamento già effettuato
  const [editDialog, setEditDialog] = useState(null); // booking item
  const [editRsrv, setEditRsrv] = useState('');
  const [editCost, setEditCost] = useState('');
  const [editTxId, setEditTxId] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editBusy, setEditBusy] = useState(false);

  // Ricevute PayPal (allegati)
  const [receipts, setReceipts] = useState([]);
  const [receiptsLoading, setReceiptsLoading] = useState(false);
  const [receiptUploading, setReceiptUploading] = useState(false);
  const [receiptLabel, setReceiptLabel] = useState('');
  const [receiptFileInputKey, setReceiptFileInputKey] = useState(0);

  const fetchReceipts = async (bookingId) => {
    if (!bookingId) { setReceipts([]); return; }
    setReceiptsLoading(true);
    try {
      const r = await api.get(`/owner-payments/paypal/${bookingId}/receipts`);
      setReceipts(r.data.receipts || []);
    } catch (e) {
      toast.error('Errore caricamento ricevute');
    } finally {
      setReceiptsLoading(false);
    }
  };

  const uploadReceipt = async (file) => {
    if (!editDialog || !file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('File troppo grande (max 5 MB)'); return; }
    setReceiptUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (receiptLabel.trim()) fd.append('label', receiptLabel.trim());
      await api.post(`/owner-payments/paypal/${editDialog.booking_id}/receipts`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Ricevuta caricata');
      setReceiptLabel('');
      setReceiptFileInputKey(k => k + 1);
      await fetchReceipts(editDialog.booking_id);
    } catch (e) {
      toast.error('Errore upload: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setReceiptUploading(false);
    }
  };

  const deleteReceipt = async (receiptId) => {
    if (!editDialog) return;
    if (!window.confirm('Eliminare questa ricevuta?')) return;
    try {
      await api.delete(`/owner-payments/paypal/${editDialog.booking_id}/receipts/${receiptId}`);
      toast.success('Ricevuta eliminata');
      await fetchReceipts(editDialog.booking_id);
    } catch (e) {
      toast.error('Errore: ' + (e?.response?.data?.detail || e.message));
    }
  };

  const downloadReceipt = (receipt) => {
    if (!editDialog) return;
    const token = localStorage.getItem('hw_token');
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/owner-payments/paypal/${editDialog.booking_id}/receipts/${receipt.id}/download`;
    // open in new tab with bearer header → use a temporary fetch + blob
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const objUrl = URL.createObjectURL(blob);
        window.open(objUrl, '_blank');
        setTimeout(() => URL.revokeObjectURL(objUrl), 60000);
      })
      .catch(e => toast.error('Errore download'));
  };

  const openEditDialog = (item) => {
    setEditDialog(item);
    setEditRsrv(item.rsrv_amount != null ? String(item.rsrv_amount) : String(item.owner_amount || ''));
    setEditCost(item.cost_amount != null ? String(item.cost_amount) : '0');
    setEditTxId(item.paypal_transaction_id || '');
    setEditNotes(item.paypal_notes || '');
    setReceiptLabel('');
    fetchReceipts(item.booking_id);
  };

  // === Ricevuta cumulativa (1 file → N booking) ===
  const [cumDialog, setCumDialog] = useState(false);
  const [cumBusy, setCumBusy] = useState(false);
  const [cumUploaded, setCumUploaded] = useState({}); // { rsrv: bool, cost: bool, otherCount: int }
  const [cumFileKey, setCumFileKey] = useState(0);
  const [cumOtherLabel, setCumOtherLabel] = useState('');

  // === Mail di cortesia: diagnostica + backfill ===
  const [emailDiag, setEmailDiag] = useState(null);
  const [emailDiagOpen, setEmailDiagOpen] = useState(false);
  const [emailDiagLoading, setEmailDiagLoading] = useState(false);
  const [emailBackfillBusy, setEmailBackfillBusy] = useState(false);

  const fetchEmailDiagnostic = async () => {
    setEmailDiagLoading(true);
    try {
      const r = await api.get('/owner-payments/paypal/email-diagnostic?days_back=3');
      setEmailDiag(r.data);
    } catch (e) {
      toast.error('Errore diagnostica: ' + (e?.response?.data?.detail || e.message));
      setEmailDiag(null);
    } finally {
      setEmailDiagLoading(false);
    }
  };

  const runBackfill = async () => {
    if (!window.confirm("Inviare ora le mail di cortesia per i pagamenti PayPal degli ultimi 2 giorni che non le hanno ricevute?")) return;
    setEmailBackfillBusy(true);
    try {
      const r = await api.post('/owner-payments/paypal/backfill-missed-emails?days_back=2');
      const f = r.data.flush_result || {};
      toast.success(`Inviate ${f.sent || 0} mail aggregate · ${r.data.marked_as_pending} booking processati`);
      await fetchEmailDiagnostic();
      fetchItems();
    } catch (e) {
      toast.error('Errore backfill: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setEmailBackfillBusy(false);
    }
  };

  // === Backfill INDIPENDENTE bonifici proprietari (più permissivo) ===
  const [ownerSepaBusy, setOwnerSepaBusy] = useState(false);
  const [ownerSepaResult, setOwnerSepaResult] = useState(null);
  const runOwnerSepaBackfill = async () => {
    if (!window.confirm("Forzare l'invio delle mail di cortesia ai PROPRIETARI per i bonifici SEPA degli ultimi 3 giorni?")) return;
    setOwnerSepaBusy(true);
    setOwnerSepaResult(null);
    try {
      const r = await api.post('/owner-payments/backfill-owner-sepa?days_back=3');
      setOwnerSepaResult(r.data);
      const f = r.data.flush_result || {};
      toast.success(`✓ Batch processati: ${r.data.batches_processed}/${r.data.batches_examined} · Mail inviate: ${f.sent || 0}`);
      await fetchEmailDiagnostic();
      fetchItems();
    } catch (e) {
      setOwnerSepaResult({ error: e?.response?.data?.detail || e.message });
      toast.error('Errore bonifici proprietari: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setOwnerSepaBusy(false);
    }
  };

  // === Backfill INDIPENDENTE fornitori (fatture pagate) ===
  const [supplierBusy, setSupplierBusy] = useState(false);
  const [supplierResult, setSupplierResult] = useState(null);
  const runSupplierBackfill = async () => {
    if (!window.confirm("Inviare ora le mail di cortesia ai FORNITORI/RESPONSABILI/PARTNER per le fatture pagate negli ultimi 3 giorni?")) return;
    setSupplierBusy(true);
    setSupplierResult(null);
    try {
      const r = await api.post('/owner-payments/backfill-supplier-sepa?days_back=3');
      setSupplierResult(r.data);
      toast.success(`✓ Gruppi: ${r.data.groups} · Mail inviate: ${r.data.sent} · Già spedite: ${r.data.skipped_already_sent}`);
    } catch (e) {
      setSupplierResult({ error: e?.response?.data?.detail || e.message });
      toast.error('Errore fornitori: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setSupplierBusy(false);
    }
  };

  const openEmailDiagDialog = () => {
    setEmailDiagOpen(true);
    fetchEmailDiagnostic();
  };

  const resetCumDialog = () => {
    setCumDialog(false);
    setCumUploaded({});
    setCumOtherLabel('');
    setCumFileKey(k => k + 1);
  };

  const uploadCumulativeReceipt = async (file, label) => {
    if (!file) return;
    if (selectedForReceipt.length === 0) {
      toast.error('Seleziona almeno una prenotazione');
      return;
    }
    if (file.size > 5 * 1024 * 1024) { toast.error('File troppo grande (max 5 MB)'); return; }
    setCumBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('booking_ids', JSON.stringify(selectedForReceipt.map(x => x.booking_id)));
      if (label) fd.append('label', label);
      const r = await api.post('/owner-payments/paypal/receipts/bulk', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Ricevuta "${label || 'cumulativa'}" allegata a ${r.data.booking_count} prenotazioni`);
      // Track stato per evitare doppio upload
      if (label === 'RSRV') setCumUploaded(s => ({...s, rsrv: true}));
      else if (label === 'COST') setCumUploaded(s => ({...s, cost: true}));
      else setCumUploaded(s => ({...s, otherCount: (s.otherCount || 0) + 1}));
      setCumFileKey(k => k + 1);
      fetchItems(); // refresh counters
    } catch (e) {
      toast.error('Errore: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setCumBusy(false);
    }
  };

  const confirmEdit = async () => {
    if (!editDialog) return;
    setEditBusy(true);
    try {
      const rsrv = parseFloat(editRsrv);
      const cost = parseFloat(editCost);
      if (isNaN(rsrv) || isNaN(cost) || rsrv < 0 || cost < 0) {
        toast.error('Importi RSRV/COST non validi');
        setEditBusy(false);
        return;
      }
      await api.patch(`/owner-payments/paypal/${editDialog.booking_id}/edit`, {
        rsrv_amount: rsrv,
        cost_amount: cost,
        paypal_transaction_id: editTxId,
        paypal_notes: editNotes,
      });
      toast.success('Pagamento aggiornato');
      setEditDialog(null);
      fetchItems();
    } catch (e) {
      toast.error('Errore: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setEditBusy(false);
    }
  };

  const fetchItems = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (includePaid) params.append('include_paid', 'true');
      if (onlyDue) params.append('only_due', 'true');
      if (includeCancelled) params.append('include_cancelled', 'true');
      if (includeZero) params.append('include_zero_amount', 'true');
      const r = await api.get(`/owner-payments/paypal-pending?${params}`);
      setItems(r.data.items || []);
    } catch (e) {
      toast.error('Errore caricamento pagamenti PayPal');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchItems(); /* eslint-disable-next-line */ }, [includePaid, onlyDue, includeCancelled, includeZero]);

  // Carica preview-split di un singolo booking (lazy)
  const loadSplitPreview = async (booking_id) => {
    if (splitPreviews[booking_id]) return splitPreviews[booking_id];
    try {
      const r = await api.get(`/owner-payments/paypal/${booking_id}/preview-split`);
      setSplitPreviews(prev => ({ ...prev, [booking_id]: r.data }));
      return r.data;
    } catch (e) {
      return null;
    }
  };

  // Carica preview-split per i pending visibili (per mostrare colonne)
  useEffect(() => {
    const pendings = (items || []).filter(x => !x.paypal_paid_at).slice(0, 50);
    if (pendings.length === 0) return;
    setSplitsLoading(true);
    Promise.all(pendings.map(p => loadSplitPreview(p.booking_id))).finally(() => setSplitsLoading(false));
    // eslint-disable-next-line
  }, [items]);

  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);

  // Applica filtri e sort
  const filtered = useMemo(() => {
    const q = (fSearch || '').toLowerCase().trim();
    const minAmt = parseFloat(fAmountMin);
    const maxAmt = parseFloat(fAmountMax);
    let r = items.filter((it) => {
      if (fOwner && !(it.owner_name || '').toLowerCase().includes(fOwner.toLowerCase())) return false;
      if (fPaypal && !(it.owner_paypal_email || '').toLowerCase().includes(fPaypal.toLowerCase())) return false;
      if (fProp && !(it.property_name || '').toLowerCase().includes(fProp.toLowerCase())) return false;
      if (fBooking && !(it.booking_code || '').toLowerCase().includes(fBooking.toLowerCase())) return false;
      if (fGuest && !(it.guest_name || '').toLowerCase().includes(fGuest.toLowerCase())) return false;
      if (fDueFrom && (!it.owner_payment_due_date || it.owner_payment_due_date < fDueFrom)) return false;
      if (fDueTo && (!it.owner_payment_due_date || it.owner_payment_due_date > fDueTo)) return false;
      if (fCheckInFrom && (!it.check_in || it.check_in.slice(0, 10) < fCheckInFrom)) return false;
      if (fCheckInTo && (!it.check_in || it.check_in.slice(0, 10) > fCheckInTo)) return false;
      if (fCheckOutFrom && (!it.check_out || it.check_out.slice(0, 10) < fCheckOutFrom)) return false;
      if (fCheckOutTo && (!it.check_out || it.check_out.slice(0, 10) > fCheckOutTo)) return false;
      if (!isNaN(minAmt) && (it.owner_amount || 0) < minAmt) return false;
      if (!isNaN(maxAmt) && (it.owner_amount || 0) > maxAmt) return false;
      if (fStatus !== 'all') {
        const paid = !!it.paypal_paid_at;
        const cancelled = !!it.cancelled;
        const overdue = !paid && !cancelled && it.owner_payment_due_date && it.owner_payment_due_date < today;
        if (fStatus === 'paid' && !paid) return false;
        if (fStatus === 'pending' && (paid || cancelled)) return false;
        if (fStatus === 'overdue' && !overdue) return false;
        if (fStatus === 'cancelled' && !cancelled) return false;
      }
      if (q) {
        const hay = [
          it.booking_code, it.guest_name, it.property_name, it.property_code,
          it.owner_name, it.owner_email, it.owner_paypal_email,
          String(it.owner_amount || ''), it.paypal_transaction_id, it.paypal_notes,
        ].filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });

    // sort
    const dir = sortDir === 'asc' ? 1 : -1;
    r.sort((a, b) => {
      const va = a[sortBy] ?? '';
      const vb = b[sortBy] ?? '';
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;
      return String(va).localeCompare(String(vb)) * dir;
    });
    return r;
  }, [items, fSearch, fOwner, fPaypal, fProp, fBooking, fGuest, fDueFrom, fDueTo, fCheckInFrom, fCheckInTo, fCheckOutFrom, fCheckOutTo, fAmountMin, fAmountMax, fStatus, sortBy, sortDir, today]);

  const totalDue = useMemo(
    () => filtered.filter((x) => !x.paypal_paid_at && !x.cancelled).reduce((s, x) => s + (x.owner_amount || 0), 0),
    [filtered]
  );
  const countDue = useMemo(() => filtered.filter((x) => !x.paypal_paid_at && !x.cancelled).length, [filtered]);

  const hasFilters = fSearch || fOwner || fPaypal || fProp || fBooking || fGuest
    || fDueFrom || fDueTo || fCheckInFrom || fCheckInTo || fCheckOutFrom || fCheckOutTo
    || fAmountMin || fAmountMax || fStatus !== 'all';

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortBy(col); setSortDir('asc'); }
  };

  const SortIcon = ({ col }) => {
    if (sortBy !== col) return <ArrowUpDown className="h-3 w-3 inline-block ml-1 opacity-40" />;
    return sortDir === 'asc'
      ? <ArrowUp className="h-3 w-3 inline-block ml-1 text-blue-600" />
      : <ArrowDown className="h-3 w-3 inline-block ml-1 text-blue-600" />;
  };

  const resetFilters = () => {
    setFSearch(''); setFOwner(''); setFPaypal(''); setFProp(''); setFBooking(''); setFGuest('');
    setFDueFrom(''); setFDueTo(''); setFCheckInFrom(''); setFCheckInTo('');
    setFCheckOutFrom(''); setFCheckOutTo(''); setFAmountMin(''); setFAmountMax('');
    setFStatus('all');
  };

  const openPaypal = (item) => {
    const email = item.owner_paypal_email;
    const amount = Number(item.owner_amount || 0).toFixed(2);
    if (!email) {
      toast.error('Email PayPal mancante sull\'immobile');
      return;
    }
    // PayPal Send Money — Personal payment (friends & family by default in EU)
    const url = `https://www.paypal.com/myaccount/transfer/homepage/send?recipient=${encodeURIComponent(email)}&amount=${amount}&currencyCode=EUR`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const openMarkDialog = (mode, list) => {
    setMarkDialog({ mode, items: list });
    setMarkTxId('');
    setMarkNotes('');
    // Pre-popola i 2 valori dal preview-split (se è single)
    if (list.length === 1) {
      const it = list[0];
      const preview = splitPreviews[it.booking_id];
      if (preview) {
        setSplitOverrides({
          [it.booking_id]: {
            rsrv: preview.rsrv_amount,
            cost: preview.cost_amount,
            originalRsrv: preview.rsrv_amount,
            originalCost: preview.cost_amount,
            debug: preview.debug,
            fallback: preview.fallback_all_rsrv,
            capReached: preview.cap_reached,
          }
        });
      }
    }
  };

  const updateSplit = (bookingId, key, value) => {
    const num = parseFloat(value);
    setSplitOverrides(prev => ({
      ...prev,
      [bookingId]: { ...(prev[bookingId] || {}), [key]: isNaN(num) ? 0 : num }
    }));
  };

  const confirmMark = async () => {
    if (!markDialog) return;
    setMarkBusy(true);
    try {
      for (const it of markDialog.items) {
        const override = splitOverrides[it.booking_id];
        const body = {
          paypal_transaction_id: markTxId || undefined,
          notes: markNotes || undefined,
        };
        // Se ho un override per questo booking, passa rsrv e cost (manuali o calcolati)
        if (override && (typeof override.rsrv === 'number' || typeof override.cost === 'number')) {
          body.rsrv_amount = override.rsrv ?? 0;
          body.cost_amount = override.cost ?? 0;
        }
        await api.post(`/owner-payments/paypal/${it.booking_id}/mark-paid`, body);
      }
      toast.success(`${markDialog.items.length} pagamento/i segnati come pagati`);
      setMarkDialog(null);
      setSelected(new Set());
      setSplitOverrides({});
      setSplitPreviews({});
      fetchItems();
    } catch (e) {
      toast.error('Errore: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setMarkBusy(false);
    }
  };

  const unmark = async (item) => {
    if (!window.confirm(`Rimuovere il flag 'pagato' per ${item.booking_code}?`)) return;
    try {
      await api.post(`/owner-payments/paypal/${item.booking_id}/unmark-paid`);
      toast.success('Stato resettato');
      fetchItems();
    } catch (e) { toast.error('Errore'); }
  };

  const toggleSelect = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const selectedItems = useMemo(
    () => filtered.filter((it) => selected.has(it.booking_id) && !it.paypal_paid_at && !it.cancelled),
    [filtered, selected]
  );
  const selectedForReceipt = useMemo(
    () => filtered.filter((it) => selected.has(it.booking_id) && !it.cancelled),
    [filtered, selected]
  );

  return (
    <div className="space-y-4" data-testid="paypal-owner-payments-tab">
      {/* Header KPI */}
      <Card className="p-4 bg-gradient-to-r from-blue-50 to-cyan-50 border-blue-200">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Wallet className="h-8 w-8 text-blue-700" />
            <div>
              <h3 className="font-bold text-blue-900 text-lg">Pagamenti PayPal proprietari</h3>
              <p className="text-sm text-blue-700">
                {countDue} pagamenti da effettuare · totale <strong>{fmtEUR(totalDue)}</strong>
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <label className="flex items-center gap-2 text-sm text-blue-900 cursor-pointer">
              <input type="checkbox" checked={onlyDue} onChange={(e) => setOnlyDue(e.target.checked)}
                data-testid="filter-only-due"
              />
              Solo scaduti / oggi
            </label>
            <label className="flex items-center gap-2 text-sm text-blue-900 cursor-pointer">
              <input type="checkbox" checked={includePaid} onChange={(e) => setIncludePaid(e.target.checked)}
                data-testid="filter-include-paid"
              />
              Mostra anche pagati
            </label>
            <label className="flex items-center gap-2 text-sm text-blue-900 cursor-pointer">
              <input type="checkbox" checked={includeCancelled} onChange={(e) => setIncludeCancelled(e.target.checked)}
                data-testid="filter-include-cancelled"
              />
              Includi cancellate
            </label>
            <label className="flex items-center gap-2 text-sm text-blue-900 cursor-pointer">
              <input type="checkbox" checked={includeZero} onChange={(e) => setIncludeZero(e.target.checked)}
                data-testid="filter-include-zero"
              />
              Importo 0
            </label>
            <Button size="sm" variant="outline" onClick={fetchItems} data-testid="refresh-paypal-list">
              <RotateCcw className="h-3.5 w-3.5 mr-1" /> Aggiorna
            </Button>
            {!readOnly && (
              <Button size="sm" variant="outline" onClick={openEmailDiagDialog} data-testid="open-email-diagnostic"
                title="Diagnostica mail di cortesia e recovery mail mancanti">
                ✉️ Mail di cortesia
              </Button>
            )}
          </div>
        </div>
      </Card>

      {/* Bulk actions */}
      {!readOnly && selectedForReceipt.length > 0 && (
        <Card className="p-3 bg-amber-50 border-amber-300 flex items-center justify-between flex-wrap gap-2">
          <span className="text-sm text-amber-900">
            <strong>{selectedForReceipt.length}</strong> selezionate
            {selectedItems.length > 0 && <> · <strong>{selectedItems.length}</strong> da pagare · totale <strong>{fmtEUR(selectedItems.reduce((s, x) => s + x.owner_amount, 0))}</strong></>}
          </span>
          <div className="flex items-center gap-2">
            {selectedItems.length > 0 && (
              <Button size="sm" onClick={() => openMarkDialog('bulk', selectedItems)} data-testid="bulk-mark-paid">
                <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Segna {selectedItems.length} come pagati
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={() => { setCumUploaded({}); setCumOtherLabel(''); setCumDialog(true); }} data-testid="bulk-attach-receipt">
              <Paperclip className="h-3.5 w-3.5 mr-1" /> Allega ricevute cumulative (RSRV + COST)
            </Button>
          </div>
        </Card>
      )}

      {/* Filters */}
      <div className="flex justify-end">
        <Button size="sm" variant={showFilters ? 'default' : 'outline'} onClick={() => setShowFilters(!showFilters)}>
          <Filter className="h-3.5 w-3.5 mr-1.5" /> Filtri
          {hasFilters && <span className="ml-1 bg-red-500 text-white rounded-full w-4 h-4 text-[10px] flex items-center justify-center">!</span>}
        </Button>
      </div>
      {showFilters && (
        <Card className="p-4">
          {/* Ricerca globale */}
          <div className="mb-3">
            <Label className="text-xs">Ricerca globale (codice, ospite, importo, IBAN, transaction ID, note...)</Label>
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
              <input type="text" value={fSearch} onChange={(e) => setFSearch(e.target.value)}
                placeholder="Cerca tutto..." className="w-full pl-8 pr-3 py-2 border rounded-lg text-sm"
                data-testid="filter-global-search" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3">
            <div>
              <Label className="text-xs">Proprietario</Label>
              <input type="text" value={fOwner} onChange={(e) => setFOwner(e.target.value)}
                placeholder="Mario Rossi..." className="w-full px-3 py-2 border rounded-lg text-sm" />
            </div>
            <div>
              <Label className="text-xs">Account PayPal</Label>
              <input type="text" value={fPaypal} onChange={(e) => setFPaypal(e.target.value)}
                placeholder="@handle o email..." className="w-full px-3 py-2 border rounded-lg text-sm" />
            </div>
            <div>
              <Label className="text-xs">Immobile</Label>
              <input type="text" value={fProp} onChange={(e) => setFProp(e.target.value)}
                placeholder="Nome immobile" className="w-full px-3 py-2 border rounded-lg text-sm" />
            </div>
            <div>
              <Label className="text-xs">Codice prenotazione</Label>
              <input type="text" value={fBooking} onChange={(e) => setFBooking(e.target.value)}
                placeholder="ILA..." className="w-full px-3 py-2 border rounded-lg text-sm" />
            </div>
            <div>
              <Label className="text-xs">Ospite</Label>
              <input type="text" value={fGuest} onChange={(e) => setFGuest(e.target.value)}
                placeholder="Nome ospite" className="w-full px-3 py-2 border rounded-lg text-sm" />
            </div>
            <div>
              <Label className="text-xs">Stato</Label>
              <select value={fStatus} onChange={(e) => setFStatus(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm bg-white" data-testid="filter-status">
                <option value="all">Tutti</option>
                <option value="pending">Da pagare</option>
                <option value="overdue">Scaduti</option>
                <option value="paid">Già pagati</option>
                <option value="cancelled">Cancellate</option>
              </select>
            </div>
            <div>
              <Label className="text-xs">Importo min (€)</Label>
              <input type="number" step="0.01" value={fAmountMin} onChange={(e) => setFAmountMin(e.target.value)}
                placeholder="0" className="w-full px-3 py-2 border rounded-lg text-sm" />
            </div>
            <div>
              <Label className="text-xs">Importo max (€)</Label>
              <input type="number" step="0.01" value={fAmountMax} onChange={(e) => setFAmountMax(e.target.value)}
                placeholder="∞" className="w-full px-3 py-2 border rounded-lg text-sm" />
            </div>
            <div>
              <Label className="text-xs">Check-in dal</Label>
              <input type="date" value={fCheckInFrom} onChange={(e) => setFCheckInFrom(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm" />
            </div>
            <div>
              <Label className="text-xs">Check-in al</Label>
              <input type="date" value={fCheckInTo} onChange={(e) => setFCheckInTo(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm" />
            </div>
            <div>
              <Label className="text-xs">Check-out dal</Label>
              <input type="date" value={fCheckOutFrom} onChange={(e) => setFCheckOutFrom(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm" />
            </div>
            <div>
              <Label className="text-xs">Check-out al</Label>
              <input type="date" value={fCheckOutTo} onChange={(e) => setFCheckOutTo(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm" />
            </div>
            <div>
              <Label className="text-xs">Scadenza dal</Label>
              <input type="date" value={fDueFrom} onChange={(e) => setFDueFrom(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm" />
            </div>
            <div>
              <Label className="text-xs">Scadenza al</Label>
              <input type="date" value={fDueTo} onChange={(e) => setFDueTo(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm" />
            </div>
            <div className="flex items-end col-span-2">
              {hasFilters && (
                <Button size="sm" variant="outline" onClick={resetFilters} data-testid="reset-filters-btn">
                  <X className="h-3.5 w-3.5 mr-1" /> Reset filtri
                </Button>
              )}
            </div>
          </div>
        </Card>
      )}

      {/* Tabella */}
      {loading ? (
        <div className="text-center py-12 text-slate-500"><Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />Caricamento...</div>
      ) : filtered.length === 0 ? (
        <Card className="p-8 text-center text-slate-400">
          <Wallet className="h-8 w-8 mx-auto mb-2" />
          <p>Nessun pagamento PayPal trovato</p>
          <p className="text-xs mt-1">
            {items.length === 0
              ? 'Imposta "PayPal" come modalità pagamento sulla scheda immobile per vederli qui'
              : 'Modifica i filtri per visualizzare i pagamenti'}
          </p>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-100 text-slate-700">
                <tr>
                  <th className="px-3 py-2 text-left w-8">
                    {!readOnly && (
                      <input type="checkbox"
                        checked={filtered.filter(x => !x.cancelled).length > 0 &&
                                 filtered.filter(x => !x.cancelled).every(x => selected.has(x.booking_id))}
                        onChange={(e) => {
                          const next = new Set(selected);
                          if (e.target.checked) filtered.forEach(x => { if (!x.cancelled) next.add(x.booking_id); });
                          else filtered.forEach(x => next.delete(x.booking_id));
                          setSelected(next);
                        }}
                      />
                    )}
                  </th>
                  <th className="px-3 py-2 text-left cursor-pointer hover:bg-slate-200" onClick={() => toggleSort('owner_name')}>
                    Proprietario <SortIcon col="owner_name" />
                  </th>
                  <th className="px-3 py-2 text-left cursor-pointer hover:bg-slate-200" onClick={() => toggleSort('owner_paypal_email')}>
                    Email PayPal <SortIcon col="owner_paypal_email" />
                  </th>
                  <th className="px-3 py-2 text-left cursor-pointer hover:bg-slate-200" onClick={() => toggleSort('property_name')}>
                    Immobile <SortIcon col="property_name" />
                  </th>
                  <th className="px-3 py-2 text-left cursor-pointer hover:bg-slate-200" onClick={() => toggleSort('booking_code')}>
                    Prenotazione <SortIcon col="booking_code" />
                  </th>
                  <th className="px-3 py-2 text-left cursor-pointer hover:bg-slate-200" onClick={() => toggleSort('check_in')}>
                    Check-in <SortIcon col="check_in" />
                  </th>
                  <th className="px-3 py-2 text-left cursor-pointer hover:bg-slate-200" onClick={() => toggleSort('check_out')}>
                    Check-out <SortIcon col="check_out" />
                  </th>
                  <th className="px-3 py-2 text-left cursor-pointer hover:bg-slate-200" onClick={() => toggleSort('owner_payment_due_date')}>
                    Scadenza <SortIcon col="owner_payment_due_date" />
                  </th>
                  <th className="px-3 py-2 text-right cursor-pointer hover:bg-slate-200" onClick={() => toggleSort('owner_amount')}>
                    Importo <SortIcon col="owner_amount" />
                  </th>
                  <th className="px-3 py-2 text-right text-xs">RSRV</th>
                  <th className="px-3 py-2 text-right text-xs">COST</th>
                  <th className="px-3 py-2 text-center">Stato</th>
                  <th className="px-3 py-2 text-center">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((it) => {
                  const paid = !!it.paypal_paid_at;
                  const cancelled = !!it.cancelled;
                  const overdue = !paid && !cancelled && it.owner_payment_due_date && it.owner_payment_due_date < today;
                  return (
                    <tr key={it.booking_id}
                      className={`border-t hover:bg-slate-50 ${cancelled ? 'opacity-50 bg-slate-50/60' : paid ? 'opacity-60' : overdue ? 'bg-red-50/40' : ''}`}
                      data-testid={`paypal-row-${it.booking_id}`}>
                      <td className="px-3 py-2">
                        {!readOnly && !cancelled && (
                          <input type="checkbox"
                            checked={selected.has(it.booking_id)}
                            onChange={() => toggleSelect(it.booking_id)}
                            title={paid ? 'Seleziona per allegare ricevuta cumulativa' : 'Seleziona per pagare'}
                          />
                        )}
                      </td>
                      <td className="px-3 py-2 font-medium">{it.owner_name || <span className="text-slate-400">—</span>}</td>
                      <td className="px-3 py-2 text-xs"><code className="bg-slate-100 px-1.5 py-0.5 rounded">{it.owner_paypal_email || '—'}</code></td>
                      <td className="px-3 py-2">{it.property_name}<br/><span className="text-xs text-slate-500">{it.property_code}</span></td>
                      <td className="px-3 py-2 text-xs">
                        <strong>{it.booking_code}</strong><br/>
                        <span className="text-slate-500">{it.guest_name}</span>
                      </td>
                      <td className="px-3 py-2 text-xs">{fmtDate(it.check_in)}</td>
                      <td className="px-3 py-2 text-xs">{fmtDate(it.check_out)}</td>
                      <td className={`px-3 py-2 text-xs font-medium ${overdue ? 'text-red-700' : ''}`}>
                        {fmtDate(it.owner_payment_due_date)}
                        {overdue && <span className="ml-1 text-red-700"><AlertTriangle className="h-3 w-3 inline" /></span>}
                      </td>
                      <td className="px-3 py-2 text-right font-bold">{fmtEUR(it.owner_amount)}</td>
                      <td className="px-3 py-2 text-right text-xs">
                        {paid ? fmtEUR(it.rsrv_amount ?? it.owner_amount) : (
                          splitPreviews[it.booking_id]
                            ? <span className="font-semibold text-blue-700">{fmtEUR(splitPreviews[it.booking_id].rsrv_amount)}</span>
                            : <span className="text-slate-400">...</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right text-xs">
                        {paid ? fmtEUR(it.cost_amount ?? 0) : (
                          splitPreviews[it.booking_id]
                            ? <span className={`font-semibold ${splitPreviews[it.booking_id].fallback_all_rsrv ? 'text-amber-600' : splitPreviews[it.booking_id].cap_reached ? 'text-orange-600' : 'text-slate-700'}`}
                                title={splitPreviews[it.booking_id].fallback_all_rsrv ? 'Budget COSTS non configurato — tutto come RSRV' : splitPreviews[it.booking_id].cap_reached ? 'Budget COSTS esaurito o parziale' : 'Calcolato'}>
                                {fmtEUR(splitPreviews[it.booking_id].cost_amount)}
                              </span>
                            : <span className="text-slate-400">...</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-center">
                        {cancelled ? (
                          <span className="bg-slate-200 text-slate-600 px-2 py-0.5 rounded-full text-xs font-medium" title="Prenotazione cancellata">
                            Cancellata
                          </span>
                        ) : paid ? (
                          <span className="bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full text-xs font-medium">
                            Pagato
                          </span>
                        ) : overdue ? (
                          <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded-full text-xs font-medium">
                            Scaduto
                          </span>
                        ) : (
                          <span className="bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full text-xs font-medium">
                            Da pagare
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1 justify-center flex-wrap">
                          {readOnly ? (
                            paid ? (
                              <Button size="sm" variant="outline" onClick={() => openEditDialog(it)}
                                data-testid={`view-paid-${it.booking_id}`}>
                                <Eye className="h-3 w-3 mr-1" /> Dettagli
                                {(it.paypal_receipts_count || 0) > 0 && (
                                  <span className="ml-1.5 bg-blue-100 text-blue-700 px-1.5 rounded-full text-[10px] font-bold inline-flex items-center">
                                    <Paperclip className="h-2.5 w-2.5 mr-0.5" />{it.paypal_receipts_count}
                                  </span>
                                )}
                              </Button>
                            ) : (
                              <span className="text-xs text-slate-400 italic">—</span>
                            )
                          ) : !paid && !cancelled ? (
                            <>
                              <Button size="sm" variant="outline" onClick={() => openPaypal(it)}
                                data-testid={`open-paypal-${it.booking_id}`}>
                                <ExternalLink className="h-3 w-3 mr-1" /> Apri PayPal
                              </Button>
                              <Button size="sm" onClick={() => openMarkDialog('single', [it])}
                                data-testid={`mark-paid-${it.booking_id}`}>
                                <CheckCircle2 className="h-3 w-3 mr-1" /> Segna pagato
                              </Button>
                            </>
                          ) : paid ? (
                            <>
                              <Button size="sm" variant="outline" onClick={() => openEditDialog(it)}
                                data-testid={`edit-paid-${it.booking_id}`}>
                                <Pencil className="h-3 w-3 mr-1" /> Modifica
                                {(it.paypal_receipts_count || 0) > 0 && (
                                  <span className="ml-1.5 bg-blue-100 text-blue-700 px-1.5 rounded-full text-[10px] font-bold inline-flex items-center"
                                    title={`${it.paypal_receipts_count} ricevuta/e allegata/e`}>
                                    <Paperclip className="h-2.5 w-2.5 mr-0.5" />{it.paypal_receipts_count}
                                  </span>
                                )}
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => unmark(it)}
                                data-testid={`unmark-paid-${it.booking_id}`}>
                                <RotateCcw className="h-3 w-3 mr-1" /> Rimetti pendente
                              </Button>
                            </>
                          ) : (
                            <span className="text-xs text-slate-400 italic">—</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Dialog conferma "Segna pagato" */}
      <Dialog open={!!markDialog} onOpenChange={(open) => { if (!open) setMarkDialog(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              <CheckCircle2 className="h-5 w-5 inline text-emerald-600 mr-2" />
              Conferma pagamento{markDialog?.items?.length > 1 ? ` (${markDialog.items.length})` : ''}
            </DialogTitle>
            <DialogDescription>
              Stai confermando di aver effettuato {markDialog?.items?.length > 1 ? 'questi pagamenti' : 'questo pagamento'} via PayPal.
              I dati di transazione sono opzionali ma consigliati per l'audit.
            </DialogDescription>
          </DialogHeader>
          {markDialog && (
            <div className="space-y-3">
              <div className="bg-slate-50 rounded p-3 max-h-40 overflow-y-auto text-sm">
                {markDialog.items.map((it) => (
                  <div key={it.booking_id} className="flex justify-between py-1 border-b last:border-0">
                    <span><strong>{it.owner_name}</strong> · {it.property_name} · {it.booking_code}</span>
                    <span className="font-bold">{fmtEUR(it.owner_amount)}</span>
                  </div>
                ))}
                <div className="flex justify-between pt-2 mt-2 border-t-2 font-bold">
                  <span>Totale</span>
                  <span>{fmtEUR(markDialog.items.reduce((s, x) => s + x.owner_amount, 0))}</span>
                </div>
              </div>

              {/* Split RSRV/COST editabile (solo per singolo booking) */}
              {markDialog.items.length === 1 && (() => {
                const it = markDialog.items[0];
                const ov = splitOverrides[it.booking_id] || {};
                const rsrv = typeof ov.rsrv === 'number' ? ov.rsrv : 0;
                const cost = typeof ov.cost === 'number' ? ov.cost : 0;
                const tot = Math.round((rsrv + cost) * 100) / 100;
                const expected = it.owner_amount || 0;
                const delta = Math.round((tot - expected) * 100) / 100;
                const isManuallyEdited = ov.originalRsrv !== undefined && (Math.abs(rsrv - ov.originalRsrv) > 0.01 || Math.abs(cost - ov.originalCost) > 0.01);

                // Quando l'admin modifica il TOTALE → RSRV si adegua (COST resta lo stesso
                // ma se nuovo_totale < cost allora COST = nuovo_totale e RSRV = 0)
                const onChangeTotal = (val) => {
                  const newTot = parseFloat(val) || 0;
                  let newCost = cost;
                  let newRsrv = newTot - cost;
                  if (newTot < cost) {
                    newCost = newTot;
                    newRsrv = 0;
                  }
                  setSplitOverrides(prev => ({
                    ...prev,
                    [it.booking_id]: { ...(prev[it.booking_id] || {}), rsrv: Math.round(newRsrv * 100) / 100, cost: Math.round(newCost * 100) / 100 }
                  }));
                };

                return (
                  <div className="bg-blue-50 border border-blue-200 rounded p-3 space-y-2">
                    <div className="text-xs font-semibold text-blue-900 mb-1">Suddivisione del pagamento</div>
                    {ov.fallback && (
                      <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
                        ⚠ Budget COSTS non configurato per quest'anno — versato tutto come RSRV
                      </div>
                    )}
                    {ov.capReached && !ov.fallback && (
                      <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                        ⚠ Budget COSTS esaurito o parziale: parte residua va in RSRV
                      </div>
                    )}
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <Label className="text-xs font-bold">Totale (€)</Label>
                        <Input type="number" step="0.01" value={tot}
                          onChange={(e) => onChangeTotal(e.target.value)}
                          className="font-bold border-blue-400"
                          data-testid="dialog-total-input" />
                        <p className="text-[10px] text-blue-700 mt-0.5">Modifica per ricalcolare RSRV automaticamente</p>
                      </div>
                      <div>
                        <Label className="text-xs">RSRV (Locazione)</Label>
                        <Input type="number" step="0.01" value={rsrv}
                          onChange={(e) => updateSplit(it.booking_id, 'rsrv', e.target.value)}
                          data-testid="dialog-rsrv-input" />
                      </div>
                      <div>
                        <Label className="text-xs">COST (Costi)</Label>
                        <Input type="number" step="0.01" value={cost}
                          onChange={(e) => updateSplit(it.booking_id, 'cost', e.target.value)}
                          data-testid="dialog-cost-input" />
                      </div>
                    </div>
                    <div className="flex justify-between items-center text-xs text-slate-600 pt-1 border-t border-blue-200">
                      <span>Verifica: RSRV+COST = <strong className={delta !== 0 ? 'text-orange-600' : 'text-emerald-700'}>{fmtEUR(tot)}</strong></span>
                      <span className="text-slate-400">Originale prenotazione: {fmtEUR(expected)}</span>
                    </div>
                    {isManuallyEdited && (
                      <div className="text-xs text-blue-700">
                        Valori modificati manualmente
                        <button type="button" className="ml-2 underline" onClick={() => {
                          setSplitOverrides(prev => ({...prev, [it.booking_id]: {...ov, rsrv: ov.originalRsrv, cost: ov.originalCost}}));
                        }}>Ripristina automatico</button>
                      </div>
                    )}
                  </div>
                );
              })()}

              <div>
                <Label>ID Transazione PayPal (opzionale)</Label>
                <Input value={markTxId} onChange={(e) => setMarkTxId(e.target.value)}
                  placeholder="es. 4K123456..." data-testid="mark-paid-txid" />
              </div>
              <div>
                <Label>Note (opzionale)</Label>
                <Input value={markNotes} onChange={(e) => setMarkNotes(e.target.value)}
                  placeholder="Note interne..." data-testid="mark-paid-notes" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setMarkDialog(null)}>Annulla</Button>
            <Button onClick={confirmMark} disabled={markBusy} data-testid="confirm-mark-paid">
              {markBusy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
              Conferma pagato
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog modifica pagamento già effettuato */}
      <Dialog open={!!editDialog} onOpenChange={(open) => { if (!open) setEditDialog(null); }}>
        <DialogContent data-testid="edit-paid-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {readOnly ? <Eye className="h-4 w-4 text-slate-600" /> : <Pencil className="h-4 w-4 text-blue-700" />}
              {readOnly ? 'Dettagli pagamento PayPal' : 'Modifica pagamento PayPal'}
            </DialogTitle>
            <DialogDescription>
              {readOnly
                ? "Visualizzazione in sola lettura. Le ricevute possono essere scaricate."
                : "Aggiorna gli importi RSRV/COST o i metadati del pagamento. La modifica viene tracciata (data + utente) e NON viene rinviata l'email di conferma al proprietario."}
            </DialogDescription>
          </DialogHeader>
          {editDialog && (
            <div className="space-y-3">
              <div className="bg-slate-50 rounded-lg p-3 text-sm space-y-1">
                <div><strong>{editDialog.property_name}</strong> · {editDialog.property_code}</div>
                <div className="text-slate-600">{editDialog.booking_code} — {editDialog.guest_name}</div>
                <div className="text-slate-600">Pagato il {fmtDate(editDialog.paypal_paid_at)} da {editDialog.paypal_paid_by || '—'}</div>
                <div className="text-slate-600">Importo originale proprietario: <strong>{fmtEUR(editDialog.owner_amount)}</strong></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">RSRV (€)</Label>
                  <Input type="number" step="0.01" min="0" value={editRsrv}
                    onChange={(e) => setEditRsrv(e.target.value)}
                    disabled={readOnly}
                    data-testid="edit-rsrv-input" />
                </div>
                <div>
                  <Label className="text-xs">COST (€)</Label>
                  <Input type="number" step="0.01" min="0" value={editCost}
                    onChange={(e) => setEditCost(e.target.value)}
                    disabled={readOnly}
                    data-testid="edit-cost-input" />
                </div>
              </div>
              <div className="text-xs text-slate-500">
                Totale: <strong>{fmtEUR((parseFloat(editRsrv) || 0) + (parseFloat(editCost) || 0))}</strong>
                {((parseFloat(editRsrv) || 0) + (parseFloat(editCost) || 0)).toFixed(2) !== Number(editDialog.owner_amount).toFixed(2) && (
                  <span className="ml-2 text-amber-700">⚠ diverso dall'importo originale ({fmtEUR(editDialog.owner_amount)})</span>
                )}
              </div>
              <div>
                <Label className="text-xs">ID transazione PayPal</Label>
                <Input value={editTxId} onChange={(e) => setEditTxId(e.target.value)}
                  placeholder="es. 1AB23456CD789012E" disabled={readOnly}
                  data-testid="edit-tx-id-input" />
              </div>
              <div>
                <Label className="text-xs">Note</Label>
                <Input value={editNotes} onChange={(e) => setEditNotes(e.target.value)}
                  placeholder="Note interne" disabled={readOnly}
                  data-testid="edit-notes-input" />
              </div>

              {/* === Ricevute PayPal === */}
              <div className="border-t pt-3 mt-3">
                <Label className="text-sm font-semibold flex items-center gap-1">
                  <Paperclip className="h-4 w-4" /> Ricevute PayPal
                  {receipts.length > 0 && <span className="ml-1 text-xs text-slate-500">({receipts.length})</span>}
                </Label>
                <div className="mt-2 space-y-2">
                  {receiptsLoading ? (
                    <div className="text-xs text-slate-500 flex items-center"><Loader2 className="h-3 w-3 animate-spin mr-2" /> Caricamento...</div>
                  ) : receipts.length === 0 ? (
                    <div className="text-xs text-slate-400 italic">Nessuna ricevuta allegata</div>
                  ) : (
                    <ul className="space-y-1.5" data-testid="receipts-list">
                      {receipts.map((r) => (
                        <li key={r.id} className="flex items-center gap-2 bg-slate-50 rounded px-2 py-1.5 text-xs" data-testid={`receipt-${r.id}`}>
                          <Paperclip className="h-3.5 w-3.5 text-slate-500 shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="font-medium truncate">{r.file_name}</div>
                            <div className="text-[10px] text-slate-500">
                              {r.label && <span className="bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded mr-1">{r.label}</span>}
                              {Math.round((r.size_bytes || 0) / 1024)} KB · {r.uploaded_by} · {fmtDate(r.uploaded_at)}
                            </div>
                          </div>
                          <Button size="sm" variant="ghost" className="h-7 px-2"
                            onClick={() => downloadReceipt(r)}
                            data-testid={`view-receipt-${r.id}`}>
                            <Eye className="h-3.5 w-3.5" />
                          </Button>
                          {!readOnly && (
                            <Button size="sm" variant="ghost" className="h-7 px-2 text-red-600 hover:bg-red-50"
                              onClick={() => deleteReceipt(r.id)}
                              data-testid={`delete-receipt-${r.id}`}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}

                  {/* Upload nuova ricevuta */}
                  {!readOnly && (
                    <div className="border border-dashed border-slate-300 rounded p-2 bg-white">
                    <div className="grid grid-cols-3 gap-2 items-end">
                      <div className="col-span-2">
                        <Label className="text-[10px] uppercase tracking-wide text-slate-500">Etichetta (opzionale)</Label>
                        <Input value={receiptLabel} onChange={(e) => setReceiptLabel(e.target.value)}
                          placeholder="es. RSRV, COST, Bonifico..."
                          className="h-8 text-xs"
                          data-testid="receipt-label-input" />
                      </div>
                      <label className="cursor-pointer">
                        <input
                          key={receiptFileInputKey}
                          type="file"
                          accept=".pdf,.png,.jpg,.jpeg,.webp,.heic,image/*,application/pdf"
                          className="hidden"
                          onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadReceipt(f); }}
                          data-testid="receipt-file-input"
                          disabled={receiptUploading}
                        />
                        <span className={`inline-flex items-center justify-center w-full h-8 rounded text-xs font-medium ${receiptUploading ? 'bg-slate-200 text-slate-500' : 'bg-blue-600 text-white hover:bg-blue-700'}`}>
                          {receiptUploading
                            ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Carico...</>
                            : <><Upload className="h-3.5 w-3.5 mr-1" /> Carica</>}
                        </span>
                      </label>
                    </div>
                    <p className="text-[10px] text-slate-400 mt-1">PDF, PNG, JPG, WebP, HEIC — max 5 MB per file</p>
                  </div>
                  )}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog(null)} disabled={editBusy}>{readOnly ? 'Chiudi' : 'Annulla'}</Button>
            {!readOnly && (
              <Button onClick={confirmEdit} disabled={editBusy} data-testid="confirm-edit-btn">
                {editBusy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                Salva modifiche
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog diagnostica mail di cortesia */}
      <Dialog open={emailDiagOpen} onOpenChange={(open) => { if (!open) setEmailDiagOpen(false); }}>
        <DialogContent className="max-w-2xl" data-testid="email-diagnostic-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              ✉️ Mail di cortesia proprietari — Diagnostica
            </DialogTitle>
            <DialogDescription>
              Verifica e recupera le mail automatiche al proprietario per pagamenti
              <strong> PayPal e Bonifico SEPA</strong>. Le mail vengono inviate
              <strong> aggregate per proprietario × giorno</strong> (1 mail riepilogativa) entro 5 minuti dal pagamento.
            </DialogDescription>
          </DialogHeader>
          {emailDiagLoading && (
            <div className="py-8 text-center text-slate-500">
              <Loader2 className="h-5 w-5 animate-spin inline-block mr-2" /> Caricamento...
            </div>
          )}
          {!emailDiagLoading && emailDiag && (
            <div className="space-y-3">
              {/* Stato config ambiente */}
              <div className={`rounded p-3 border ${emailDiag.would_send_emails ? 'bg-emerald-50 border-emerald-300' : 'bg-red-50 border-red-300'}`}>
                <div className="flex items-center gap-2 mb-1">
                  {emailDiag.would_send_emails
                    ? <span className="text-emerald-700 font-semibold flex items-center gap-1"><CheckCircle2 className="h-4 w-4" /> Sistema mail ATTIVO</span>
                    : <span className="text-red-700 font-semibold flex items-center gap-1"><AlertTriangle className="h-4 w-4" /> Sistema mail BLOCCATO (safeguard)</span>}
                </div>
                <div className="text-xs space-y-0.5 text-slate-700">
                  <div>Variabile <code className="bg-white px-1 rounded">ENVIRONMENT</code>: <strong>{emailDiag.environment_var}</strong></div>
                  <div>FRONTEND_URL: <code className="bg-white px-1 rounded text-[11px]">{emailDiag.frontend_url}</code></div>
                  {emailDiag.env_mismatch_details && (
                    <div className="mt-1.5 bg-red-100 text-red-800 px-2 py-1 rounded text-[11px]">
                      ⚠ Le mail NON saranno inviate finché <code>ENVIRONMENT</code> non sarà "production" (o assente) in questo ambiente.
                    </div>
                  )}
                </div>
              </div>

              {/* Breakdown ultimi 3 giorni */}
              <div className="bg-slate-50 rounded p-3">
                <div className="text-sm font-semibold mb-2">Riepilogo pagamenti ultimi 3 giorni (PayPal + Bonifico SEPA)</div>
                <div className="grid grid-cols-3 gap-2 text-sm">
                  <div className="bg-white p-2 rounded border">
                    <div className="text-xs text-slate-500">Totale pagati</div>
                    <div className="font-bold text-lg">{emailDiag.breakdown_last_days?.total || 0}</div>
                    <div className="text-[10px] text-slate-400 mt-1">
                      💰 PayPal: {emailDiag.breakdown_last_days?.paypal?.total || 0} ·
                      🏦 Bonifico: {emailDiag.breakdown_last_days?.bank_transfer?.total || 0}
                    </div>
                  </div>
                  <div className="bg-white p-2 rounded border">
                    <div className="text-xs text-emerald-600">Mail inviate</div>
                    <div className="font-bold text-lg text-emerald-700">{emailDiag.breakdown_last_days?.sent || 0}</div>
                  </div>
                  <div className="bg-white p-2 rounded border">
                    <div className="text-xs text-amber-600">In coda (pending)</div>
                    <div className="font-bold text-lg text-amber-700">{emailDiag.breakdown_last_days?.pending || 0}</div>
                  </div>
                  <div className="bg-white p-2 rounded border">
                    <div className="text-xs text-red-600">Mail di cortesia da inviare</div>
                    <div className="font-bold text-lg text-red-700">{emailDiag.breakdown_last_days?.missed || 0}</div>
                    <div className="text-[10px] text-slate-400 mt-1">
                      💰 {emailDiag.breakdown_last_days?.paypal?.missed || 0} ·
                      🏦 {emailDiag.breakdown_last_days?.bank_transfer?.missed || 0}
                    </div>
                  </div>
                  <div className="bg-white p-2 rounded border">
                    <div className="text-xs text-slate-500">Senza email proprietario in rubrica</div>
                    <div className="font-bold text-lg text-slate-600">{emailDiag.breakdown_last_days?.no_owner_email || 0}</div>
                  </div>
                  <div className="bg-white p-2 rounded border">
                    <div className="text-xs text-slate-500">Importo 0</div>
                    <div className="font-bold text-lg text-slate-600">{emailDiag.breakdown_last_days?.zero_amount || 0}</div>
                  </div>
                </div>
              </div>

              {/* Lista mail mancanti */}
              {(emailDiag.missed_emails_preview || []).length > 0 && (
                <div className="border rounded p-3">
                  <div className="text-sm font-semibold mb-2">Pagamenti senza mail di cortesia inviata</div>
                  <p className="text-[11px] text-slate-500 mb-2">L'email del proprietario c'è — manca solo l'invio della mail di conferma pagamento.</p>
                  <ul className="text-xs space-y-1 max-h-56 overflow-y-auto">
                    {(emailDiag.missed_emails_preview || []).map((m, i) => (
                      <li key={i} className="flex items-center justify-between border-b last:border-b-0 py-1 gap-2">
                        <div className="min-w-0 flex-1">
                          <span className={`inline-block mr-1.5 px-1.5 py-0.5 rounded text-[10px] font-semibold ${m.method === 'bank_transfer' ? 'bg-indigo-100 text-indigo-700' : 'bg-amber-100 text-amber-700'}`}>
                            {m.method === 'bank_transfer' ? '🏦 Bonifico' : '💰 PayPal'}
                          </span>
                          <strong className="font-mono">{m.booking_code}</strong> — {m.property_name}
                          <span className="text-slate-400 ml-2">→ {m.owner_email}</span>
                        </div>
                        <span className="text-slate-500 whitespace-nowrap">{fmtEUR(m.owner_amount)} · {fmtDate(m.paid_at || m.paypal_paid_at)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Bottone recupero */}
              {(emailDiag.breakdown_last_days?.missed || 0) > 0 && emailDiag.would_send_emails && (
                <div className="bg-blue-50 border border-blue-300 rounded p-3 text-center">
                  <p className="text-sm text-blue-900 mb-2">
                    Ci sono <strong>{emailDiag.breakdown_last_days.missed} mail di cortesia</strong> da inviare.
                  </p>
                  <Button onClick={runBackfill} disabled={emailBackfillBusy} data-testid="run-backfill-btn">
                    {emailBackfillBusy
                      ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Invio in corso...</>
                      : <>✉️ Invia ora le mail di cortesia</>}
                  </Button>
                  <p className="text-[11px] text-blue-700 mt-1.5">Le mail saranno raggruppate per proprietario × giorno.</p>
                </div>
              )}
              {(emailDiag.breakdown_last_days?.missed || 0) === 0 && (
                <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-center text-sm text-emerald-700">
                  ✓ Nessuna mail di cortesia da inviare negli ultimi 3 giorni.
                </div>
              )}

              {/* Bottoni indipendenti per bonifici proprietari + fornitori */}
              {emailDiag.would_send_emails && (
                <div className="border-2 border-dashed border-indigo-200 rounded p-3 bg-indigo-50/40">
                  <div className="text-sm font-semibold text-indigo-900 mb-2">🏦 Bonifici SEPA — Forza invio diretto</div>
                  <p className="text-[11px] text-indigo-700 mb-3">
                    Se il pannello sopra non rileva correttamente i bonifici, usa questi bottoni per processare tutti i batch SEPA executed degli ultimi 3 giorni indipendentemente da come è classificato il <code>batch_type</code>.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <Button onClick={runOwnerSepaBackfill} disabled={ownerSepaBusy}
                      className="bg-indigo-600 hover:bg-indigo-700 text-white"
                      data-testid="backfill-owner-sepa-btn">
                      {ownerSepaBusy
                        ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Invio in corso...</>
                        : <>🏦 Mail bonifici PROPRIETARI</>}
                    </Button>
                    <Button onClick={runSupplierBackfill} disabled={supplierBusy}
                      className="bg-blue-700 hover:bg-blue-800 text-white"
                      data-testid="backfill-supplier-btn">
                      {supplierBusy
                        ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Invio in corso...</>
                        : <>📄 Mail bonifici FORNITORI</>}
                    </Button>
                  </div>

                  {/* Esito dettagliato bottoni */}
                  {ownerSepaResult && (
                    <div className={`mt-3 rounded p-2.5 text-xs ${ownerSepaResult.error ? 'bg-red-50 border border-red-300 text-red-900' : 'bg-white border border-indigo-200 text-indigo-900'}`}>
                      <div className="font-semibold mb-1">🏦 Risultato Bonifici PROPRIETARI</div>
                      {ownerSepaResult.error ? (
                        <div>Errore: {ownerSepaResult.error}</div>
                      ) : (
                        <div className="space-y-0.5">
                          <div>• Batch esaminati: <strong>{ownerSepaResult.batches_examined}</strong></div>
                          <div>• Batch processati: <strong>{ownerSepaResult.batches_processed}</strong></div>
                          <div>• Booking marcati in coda: <strong>{ownerSepaResult.sepa_summary?.queued || 0}</strong></div>
                          <div>• Booking già processati (skipped): <strong>{ownerSepaResult.sepa_summary?.skipped || 0}</strong></div>
                          <div className="pt-1 border-t border-indigo-100 mt-1">
                            <div>📧 <strong>Mail aggregate inviate:</strong> {ownerSepaResult.flush_result?.sent || 0}</div>
                            <div>📧 Mail fallite: {ownerSepaResult.flush_result?.failed || 0}</div>
                            <div>📧 Gruppi (proprietario × giorno × metodo): {ownerSepaResult.flush_result?.groups || 0}</div>
                            {ownerSepaResult.flush_result?.skipped_no_owner_email ? (
                              <div className="text-amber-700">⚠ Saltati per email mancante: {ownerSepaResult.flush_result.skipped_no_owner_email}</div>
                            ) : null}
                            {ownerSepaResult.flush_result?.skipped_env ? (
                              <div className="text-red-700">⚠ Saltato: ambiente preview (mail bloccate)</div>
                            ) : null}
                          </div>
                          {(ownerSepaResult.examined_preview || []).length > 0 && (
                            <details className="mt-2">
                              <summary className="cursor-pointer text-indigo-600">Mostra batch esaminati ({ownerSepaResult.examined_preview.length})</summary>
                              <ul className="mt-1 ml-3 space-y-0.5 text-[11px] font-mono">
                                {ownerSepaResult.examined_preview.map((b, i) => (
                                  <li key={i}>• {b.batch_type || '?'} → {b.executed_at?.slice(0, 16) || '—'}</li>
                                ))}
                              </ul>
                            </details>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {supplierResult && (
                    <div className={`mt-3 rounded p-2.5 text-xs ${supplierResult.error || supplierResult.skipped_env ? 'bg-red-50 border border-red-300 text-red-900' : 'bg-white border border-blue-200 text-blue-900'}`}>
                      <div className="font-semibold mb-1">📄 Risultato Bonifici FORNITORI</div>
                      {supplierResult.error ? (
                        <div>Errore: {supplierResult.error}</div>
                      ) : supplierResult.skipped_env ? (
                        <div>⚠ Saltato: ambiente preview (mail bloccate)</div>
                      ) : (
                        <div className="space-y-0.5">
                          <div>• Batch fatture esaminati: <strong>{supplierResult.batches_examined}</strong></div>
                          <div>• Transazioni esaminate: <strong>{supplierResult.transactions_examined}</strong></div>
                          <div>• Gruppi (fornitore × giorno): <strong>{supplierResult.groups}</strong></div>
                          <div className="pt-1 border-t border-blue-100 mt-1">
                            <div>📧 <strong>Mail inviate:</strong> {supplierResult.sent}</div>
                            <div>📧 Mail fallite: {supplierResult.failed}</div>
                            {supplierResult.skipped_already_sent ? (
                              <div className="text-slate-600">✓ Già spedite in precedenza: {supplierResult.skipped_already_sent}</div>
                            ) : null}
                            {supplierResult.skipped_no_email ? (
                              <div className="text-amber-700">⚠ Saltate per email destinatario mancante: {supplierResult.skipped_no_email}</div>
                            ) : null}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <p className="text-[11px] text-indigo-600 mt-2 italic">
                    Ognuno processa indipendentemente; entrambi rispettano l'idempotenza (non re-invia mail già spedite).
                  </p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={fetchEmailDiagnostic} disabled={emailDiagLoading}>
              <RotateCcw className="h-3.5 w-3.5 mr-1" /> Aggiorna
            </Button>
            <Button variant="outline" onClick={() => setEmailDiagOpen(false)}>Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog ricevute cumulative (RSRV + COST + altro) */}
      <Dialog open={cumDialog} onOpenChange={(open) => { if (!open) resetCumDialog(); }}>        <DialogContent data-testid="cumulative-receipt-dialog" className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Paperclip className="h-4 w-4 text-blue-700" /> Ricevute cumulative
            </DialogTitle>
            <DialogDescription>
              PayPal genera 2 invii separati per pagamento cumulativo (uno con causale RSRV e uno con causale COST).
              Carica entrambe le ricevute: vengono allegate a tutte le prenotazioni selezionate.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-900">
              <strong>{selectedForReceipt.length}</strong> prenotazioni selezionate
              {selectedForReceipt.length > 0 && (
                <ul className="mt-1.5 max-h-24 overflow-y-auto space-y-0.5">
                  {selectedForReceipt.slice(0, 6).map(x => (
                    <li key={x.booking_id} className="font-mono text-[11px]">• {x.booking_code} — {x.property_name} · {fmtEUR(x.owner_amount)}</li>
                  ))}
                  {selectedForReceipt.length > 6 && <li className="italic text-amber-700">…e altre {selectedForReceipt.length - 6}</li>}
                </ul>
              )}
              <div className="mt-2 font-semibold">Totale: {fmtEUR(selectedForReceipt.reduce((s, x) => s + (x.owner_amount || 0), 0))}</div>
            </div>

            {/* Slot RSRV */}
            <ReceiptUploadSlot
              label="RSRV"
              description="Ricevuta della parte Locazione (Reservation)"
              colorClass="border-blue-400 bg-blue-50/40"
              uploaded={!!cumUploaded.rsrv}
              busy={cumBusy}
              fileKey={`rsrv-${cumFileKey}`}
              onPick={(f) => uploadCumulativeReceipt(f, 'RSRV')}
              testId="cumulative-rsrv-upload"
            />

            {/* Slot COST */}
            <ReceiptUploadSlot
              label="COST"
              description="Ricevuta della parte Costi (Costs)"
              colorClass="border-orange-400 bg-orange-50/40"
              uploaded={!!cumUploaded.cost}
              busy={cumBusy}
              fileKey={`cost-${cumFileKey}`}
              onPick={(f) => uploadCumulativeReceipt(f, 'COST')}
              testId="cumulative-cost-upload"
            />

            {/* Slot Altro (opzionale con label custom) */}
            <div className="border border-dashed border-slate-300 rounded p-2.5 bg-slate-50/40">
              <div className="text-xs font-semibold text-slate-700 mb-1.5 flex items-center justify-between">
                <span>📎 Altri allegati (opzionale)</span>
                {(cumUploaded.otherCount || 0) > 0 && <span className="bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded text-[10px]">{cumUploaded.otherCount} caricato/i</span>}
              </div>
              <p className="text-[11px] text-slate-500 mb-2">Es. screenshot riepilogativo, ricevuta bonifico esterno, ecc.</p>
              <div className="grid grid-cols-3 gap-2 items-end">
                <div className="col-span-2">
                  <Input value={cumOtherLabel} onChange={(e) => setCumOtherLabel(e.target.value)}
                    placeholder="Etichetta (es. Riepilogo bonifico)"
                    className="h-8 text-xs"
                    data-testid="cumulative-other-label" />
                </div>
                <label className="cursor-pointer">
                  <input
                    key={`other-${cumFileKey}`}
                    type="file"
                    accept=".pdf,.png,.jpg,.jpeg,.webp,.heic,image/*,application/pdf"
                    className="hidden"
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadCumulativeReceipt(f, cumOtherLabel.trim() || 'Altro'); }}
                    disabled={cumBusy || selectedForReceipt.length === 0}
                    data-testid="cumulative-other-upload"
                  />
                  <span className={`inline-flex items-center justify-center w-full h-8 rounded text-xs font-medium ${cumBusy || selectedForReceipt.length === 0 ? 'bg-slate-200 text-slate-500' : 'bg-slate-700 text-white hover:bg-slate-800'}`}>
                    <Upload className="h-3.5 w-3.5 mr-1" /> Carica
                  </span>
                </label>
              </div>
            </div>

            <p className="text-[11px] text-slate-400 text-center pt-1">
              PDF, PNG, JPG, WebP, HEIC — max 5 MB per file · Puoi caricarle anche in momenti diversi
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={resetCumDialog} disabled={cumBusy}>Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Componente helper per gli slot di upload pre-etichettati
function ReceiptUploadSlot({ label, description, colorClass, uploaded, busy, fileKey, onPick, testId }) {
  return (
    <div className={`border rounded p-2.5 ${colorClass}`}>
      <div className="flex items-center justify-between mb-1.5">
        <div>
          <div className="font-semibold text-sm flex items-center gap-1.5">
            <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${label === 'RSRV' ? 'bg-blue-600 text-white' : 'bg-orange-500 text-white'}`}>{label}</span>
            <span className="text-slate-700">{description}</span>
          </div>
        </div>
        {uploaded && (
          <span className="bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full text-[10px] font-medium inline-flex items-center">
            <CheckCircle2 className="h-3 w-3 mr-0.5" /> Caricato
          </span>
        )}
      </div>
      <label className="cursor-pointer block">
        <input
          key={fileKey}
          type="file"
          accept=".pdf,.png,.jpg,.jpeg,.webp,.heic,image/*,application/pdf"
          className="hidden"
          onChange={(e) => { const f = e.target.files?.[0]; if (f) onPick(f); }}
          disabled={busy}
          data-testid={testId}
        />
        <span className={`inline-flex items-center justify-center w-full h-8 rounded text-xs font-medium ${busy ? 'bg-slate-200 text-slate-500' : uploaded ? 'bg-white border border-slate-300 text-slate-700 hover:bg-slate-50' : 'bg-blue-600 text-white hover:bg-blue-700'}`}>
          {busy
            ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Carico...</>
            : uploaded
              ? <><Upload className="h-3.5 w-3.5 mr-1" /> Sostituisci / aggiungi un altro</>
              : <><Upload className="h-3.5 w-3.5 mr-1" /> Carica file {label}</>}
        </span>
      </label>
    </div>
  );
}
